// client/src/utils/browserLocation.js
//
// THE ONE WAY THE WEB APP ASKS WHERE IT IS — the browser half of it.
//
// The counterpart of apps/mobile/src/utils/locationFix.js, and deliberately the
// same two exports with the same signatures, so the four screens that read a
// position now differ by an import line and nothing else.
//
// THIS FILE IS WHERE THE 1KM BUG LIVED. Both web screens used to inline their
// own sampling loop, and the register's copy had an 8-second window and no
// two-sample floor — so it called `fixIsGoodEnough` on the very first reading,
// which returns true outright for anything under 30m, and a browser's first
// reading is a wifi/cell-tower lookup that routinely claims ±20-25m while being
// hundreds of metres out. Sample one, wrong place, teacher refused. Every time.
//
// The loop is gone from here. It lives in ./captureFix (generated from
// packages/core/src/geo/captureFix.js, which the phone imports directly), and
// what is left is only the part that knows what `navigator.geolocation` is
// called. tools/geofence-conformance.mjs asserts that this is the only file
// under client/src that touches it.

import { captureBestFix, captureCentreFix } from './captureFix';
import { FIX_WINDOW_MS } from './geofence';

/**
 * `navigator.geolocation`, in the shape captureFix expects.
 *
 * `enableHighAccuracy` asks the browser for GPS rather than the network
 * database. It is a request, not a guarantee — a desktop has no GPS at all and
 * will answer from wifi whatever it is asked, which is exactly why the shared
 * routine judges a stream by how its readings BEHAVE rather than by what the
 * browser claims about them.
 *
 * `maximumAge: 0` — never a cached fix. A geofence decision made from a
 * position recorded half a minute ago is a decision about where the teacher WAS.
 */
const browserProvider = {
  watch(onSample, onError) {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      throw new Error('no geolocation');
    }
    const id = navigator.geolocation.watchPosition(
      (pos) => onSample(toSample(pos)),
      onError,
      { enableHighAccuracy: true, timeout: FIX_WINDOW_MS, maximumAge: 0 },
    );
    return () => {
      try { navigator.geolocation.clearWatch(id); } catch (_) { /* already gone */ }
    };
  },
  oneShot() {
    return new Promise((resolve, reject) => {
      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        reject(new Error('no geolocation'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve(toSample(pos)),
        reject,
        { enableHighAccuracy: true, timeout: FIX_WINDOW_MS, maximumAge: 0 },
      );
    });
  },
};

const toSample = (pos) => ({
  latitude: pos?.coords?.latitude,
  longitude: pos?.coords?.longitude,
  accuracy: Number(pos?.coords?.accuracy) || 0,
});

/** Whether this browser can answer at all. Checked before offering to try. */
export function geolocationAvailable() {
  return typeof navigator !== 'undefined' && Boolean(navigator.geolocation);
}

/**
 * The most accurate fix obtainable within the shared window.
 *
 * Same signature as the phone's, on purpose.
 *
 * @param {{radius:number, latitude:number, longitude:number}} [target]
 * @param {(fix) => void} [onProgress]
 * @param {{ purpose?: 'check'|'centre' }} [options]
 */
export function bestPositionFix(target, onProgress, options = {}) {
  return captureBestFix(browserProvider, {
    target,
    onProgress,
    purpose: options.purpose === 'centre' ? 'centre' : 'check',
  });
}

/** The school's centre: the same routine, held to the centre standard. */
export function schoolCentreFix(geofenceRadius, onProgress) {
  return captureCentreFix(browserProvider, geofenceRadius, onProgress);
}
